/**
 * The world (PRD §9, §31). One continuous descent from the coastal shelf to the
 * ancient structure at −2500 m, traversable in both directions the whole way.
 *
 *   START ─ WRECK ─ MINEFIELD ─ CAVE ─ PATROL ─ TRENCH ─ OCTOPUS
 *         ─ KELP FOREST ─ SUNKEN CITY ─ MILITARY ZONE ─ DREADNOUGHT
 *         ─ THERMAL VENTS ─ THE ABYSS ─ ANCIENT STRUCTURE
 *
 * Everything here is data. Landmarks are authored; the rock between them comes
 * from the procedural sectors in `field.ts`, so the world is deterministic from
 * WORLD_SEED without ever reading as randomly generated.
 */

import type { EnemyType, PickupType } from './config';

export type AuthoredFeature = {
  kind: 'tunnel' | 'blob' | 'chamber';
  /** tunnel: flat [x, y, r, ...] polyline */
  pts?: number[];
  cx?: number; cy?: number; rx?: number; ry?: number;
};

/** Authored seabed control points [x, y]. Depth increases to the right (PRD §9). */
export const PROFILE: [number, number][] = [
  [-1200, -26], [-600, -34], [-200, -44], [0, -54],
  [180, -62], [380, -74], [520, -84], [660, -98],
  [880, -114], [1060, -130], [1240, -152], [1420, -166],
  [1600, -180], [1800, -208], [2000, -234], [2120, -246],
  [2400, -252], [2700, -258],

  // ── Out of the trench and up onto a sunlit shelf. The descent is not
  //    monotonic on purpose: after the octopus the game owes the player a
  //    bright room, and at 100 m the sun still gets through. ──
  [2760, -212], [2820, -152], [2900, -112], [3040, -96],
  [3240, -104], [3400, -132], [3460, -168],

  // ── Then the ground falls away into the drowned city. ──
  [3540, -240], [3620, -338], [3820, -428], [4060, -490], [4300, -548],

  // ── Military zone: down onto the abyssal plain. ──
  [4500, -606], [4740, -684], [5000, -798], [5240, -904],

  // ── Thermal vents. ──
  [5420, -1012], [5660, -1138], [5900, -1286], [6100, -1420],

  // ── The rift: the second great descent. ──
  [6180, -1520], [6300, -1662], [6440, -1790], [6600, -1902],
  [6800, -1994], [7000, -2090],

  // ── Ancient structure, on the floor of everything. ──
  [7100, -2182], [7260, -2300], [7440, -2412], [7640, -2492],
  [7900, -2540], [8600, -2566],
];

/**
 * Smooth monotone interpolation through the authored control points. This lives
 * here rather than in `field.ts` because the landmarks below are positioned
 * relative to it, and `field` already imports this module.
 */
export function profileY(x: number): number {
  const p = PROFILE;
  if (x <= p[0][0]) return p[0][1];
  const last = p.length - 1;
  if (x >= p[last][0]) return p[last][1];
  let i = 0;
  while (i < last && p[i + 1][0] < x) i++;
  const [xa, ya] = p[i];
  const [xb, yb] = p[i + 1];
  let t = (x - xa) / (xb - xa);
  t = t * t * (3 - 2 * t);
  return ya + (yb - ya) * t;
}

// ---------------------------------------------------------------------------
// Landmark construction helpers
//
// The authored list is built in code rather than typed out literally: a city
// block is a dozen features that all have to agree with each other, and the
// helper is both shorter and much harder to get subtly wrong.
//
// Everything east of the octopus is placed relative to `profileY` rather than
// at a literal depth, so the zone layouts stay attached to the seabed if the
// descent is ever re-tuned.
// ---------------------------------------------------------------------------

/** y at `lift` metres above the authored seabed. */
const up = (x: number, lift: number) => profileY(x) + lift;

const F: AuthoredFeature[] = [];

const blob = (cx: number, cy: number, rx: number, ry: number) => F.push({ kind: 'blob', cx, cy, rx, ry });
const chamber = (cx: number, cy: number, rx: number, ry: number) => F.push({ kind: 'chamber', cx, cy, rx, ry });
const tunnel = (pts: number[]) => F.push({ kind: 'tunnel', pts });

/**
 * A ruined tower: a stack of rock rising off the floor, hollowed by a vertical
 * shaft and punched through horizontally so it can be flown into rather than
 * only around.
 */
function tower(x: number, baseY: number, w: number, h: number, hollow = true) {
  const top = baseY + h;
  blob(x, baseY + h * 0.5, w, h * 0.55);
  blob(x + w * 0.15, top - h * 0.12, w * 0.72, h * 0.16);
  if (!hollow) return;
  tunnel([x, baseY + h * 0.15, w * 0.34, x, top - h * 0.16, w * 0.3]);
  tunnel([x - w * 1.1, baseY + h * 0.52, w * 0.3, x + w * 1.1, baseY + h * 0.52, w * 0.3]);
}

/** A rock arch you can fly under — two legs and a span. */
function arch(x: number, baseY: number, span: number, h: number) {
  blob(x, baseY + h * 0.5, span * 0.6, h * 0.62);
  tunnel([x - span * 0.3, baseY + h * 0.2, h * 0.24, x + span * 0.3, baseY + h * 0.2, h * 0.24]);
}

/**
 * Height that is reliably open water: the procedural relief adds up to about
 * 33 m on top of the authored curve, so a mouth authored just under `profileY`
 * can easily end up buried under the real seabed.
 */
const DAYLIGHT = 40;

/** A den carved back into a rock mass — where an ambusher waits. */
function den(x: number, y: number, r: number) {
  blob(x, y, r * 2.1, r * 1.6);
  chamber(x, y, r * 1.15, r * 0.85);
  // The mouth has to reach open water, not merely the edge of the blob.
  const mx = x - r * 2.6;
  tunnel([x, y, r * 0.7, mx, y, r * 0.62, mx - r * 0.7, up(mx, DAYLIGHT), r * 0.6]);
}

/** A sealed room with a shaft that always breaks the surface of the seabed. */
function vault(x: number, lift: number, rx: number, ry: number, shaftR = 9) {
  chamber(x, up(x, lift), rx, ry);
  tunnel([x, up(x, DAYLIGHT), shaftR * 0.8, x, up(x, lift + ry * 0.4), shaftR]);
}

// ── The western channels (x ≈ -1290, -1940, -2160). ──
//
// The west is not one shelf, it is a chain of pockets. Three headlands rise
// clean out of the water — measured at 0 m of gap floor-to-surface — and the
// audit's flood fill said so immediately: the reef outpost was "walled off from
// the spawn" because there was no way past the first one.
//
// Cut through rather than flattened. A headland that breaches the surface is
// the best landmark this map has, and swimming a channel under one is a better
// arrival at the reef than an open plain would be — you pass through a gate and
// the colour starts. Radii are 11-13 m against a sub of 1.9: wide enough to tow
// a crate through without threading a needle, which the cargo audit checks.
tunnel([-1250, -30, 12, -1288, -26, 13, -1330, -30, 12]);
tunnel([-1900, -26, 11, -1938, -22, 12.5, -1976, -26, 11]);
tunnel([-2120, -24, 11, -2158, -21, 12.5, -2196, -24, 11]);
// A fourth, under the shallow bar at -2240..-2310 where the floor climbs to
// -3.6 and leaves 2 m of water over it. Not a headland — a sill — and the
// flood fill needs 3 m of clearance, so it read as the end of the map. The
// meadow is on the far side of it.
tunnel([-2226, -16, 10, -2274, -14, 11, -2322, -16, 10]);

// ── The cave (x ≈ 1310 → 1665). Two mouths, a dip, and a side chamber. ──
tunnel([
  1306, -146, 9.5,
  1362, -180, 11,
  1428, -174, 9,
  1492, -199, 10.5,
  1562, -190, 12,
  1626, -163, 10,
  1668, -145, 9,
]);
chamber(1492, -206, 19, 15);
tunnel([1492, -199, 7, 1492, -212, 9]);

// ── The trench: a vertical shaft down from the seabed into the deep. ──
tunnel([
  2168, -214, 24,
  2158, -300, 26,
  2176, -400, 28,
  2214, -492, 30,
  2300, -556, 32,
  2404, -578, 34,
]);
// ── Octopus arena at the bottom of the trench. ──
chamber(2496, -560, 132, 74);
chamber(2586, -538, 74, 58);
chamber(2384, -598, 34, 26);
chamber(2612, -594, 40, 24);

// ── The climb out: an eastern chimney from the arena up into the daylight. ──
tunnel([
  2648, -580, 16,
  2700, -520, 15,
  2734, -430, 14,
  2758, -344, 14,
  2782, -256, 15,
  2812, -178, 15,
  2846, -128, 16,
]);

// ── Rock buttresses framing the wreck site. ──
blob(596, -84, 9, 20);
blob(704, -88, 11, 17);

// ---------------------------------------------------------------------------
// KELP FOREST  (2720 → 3520)
// Open, bright, generous. The relief after the octopus: the one stretch of the
// game with more to look at than to shoot.
// ---------------------------------------------------------------------------

arch(2900, up(2900, -6), 46, 30);
arch(3120, up(3120, -6), 58, 36);
arch(3350, up(3350, -6), 40, 26);
den(3010, up(3010, -4), 13);
den(3268, up(3268, -4), 15);
den(3436, up(3436, -4), 14);
// A shallow grotto with the zone's hidden reward.
vault(3186, -46, 26, 17, 8);

// ---------------------------------------------------------------------------
// SUNKEN CITY  (3520 → 4400)
// A drowned skyline: towers to weave through, with the streets between them
// covered by turrets bolted to the walls.
// ---------------------------------------------------------------------------

/** [x, half-width, height]. Towers are rooted well under the floor so no amount
 *  of procedural relief ever leaves one standing on nothing. */
const CITY: [number, number, number][] = [
  [3580, 15, 74], [3672, 12, 58], [3742, 17, 92],
  [3856, 13, 64], [3944, 18, 104], [4046, 12, 52],
  [4126, 16, 86], [4232, 14, 70], [4324, 17, 96],
];
for (const [x, w, h] of CITY) tower(x, up(x, -26), w, h);

// A great hall on the eastern edge, half buried — the city's reward room.
chamber(4300, up(4300, -52), 54, 30);
tunnel([
  4262, up(4262, DAYLIGHT), 11,
  4276, up(4276, -10), 12,
  4290, up(4290, -42), 13,
  4330, up(4330, -48), 12,
]);
// A collapsed boulevard: a long low tunnel under the rubble.
tunnel([
  3620, up(3620, -32), 13,
  3760, up(3760, -34), 14,
  3900, up(3900, -34), 14,
  4040, up(4040, -34), 15,
  4180, up(4180, -32), 14,
]);

// ---------------------------------------------------------------------------
// MILITARY ZONE  (4400 → 5300)
// Bunkers, wire and gun emplacements, opening onto the dreadnought's berth.
// ---------------------------------------------------------------------------

blob(4480, up(4480, 8), 26, 22);
blob(4560, up(4560, 14), 18, 30);
blob(4690, up(4690, 6), 30, 20);
blob(4820, up(4820, 16), 22, 34);
blob(4930, up(4930, 10), 28, 24);
// Two hardened bunkers with the ammunition inside.
den(4612, up(4612, -6), 17);
den(4884, up(4884, -6), 19);
vault(4740, -30, 30, 18, 10);
tunnel([4706, up(4706, -6), 9, 4740, up(4740, -26), 11, 4778, up(4778, -22), 9]);

// The berth: a bowl the capital sub sits in, walled at both ends so the fight
// has somewhere to happen and the player cannot simply drive past it.
blob(5008, up(5008, 24), 34, 62);
blob(5330, up(5330, 24), 38, 66);
chamber(5170, up(5170, 34), 150, 62);
tunnel([4990, up(4990, 40), 19, 5060, up(5060, 30), 22]);
tunnel([5300, up(5300, 30), 20, 5372, up(5372, 26), 20]);

// ---------------------------------------------------------------------------
// THERMAL VENTS  (5300 → 6150)
// Chimneys, plumes and unstable ground. The hazard here is the water itself.
// ---------------------------------------------------------------------------

export const VENT_X: number[] = [];
for (let i = 0; i < 9; i++) {
  const x = 5440 + i * 82;
  VENT_X.push(x);
  // Chimney stacks: tall, narrow, and close enough together to thread.
  blob(x, up(x, 24), 7 + (i % 3) * 2.5, 30 + (i % 4) * 9);
  if (i % 3 === 1) blob(x + 26, up(x + 26, 13), 5.5, 20);
}
den(5620, up(5620, -6), 18);
den(5980, up(5980, -6), 20);
vault(5820, -34, 34, 20, 10);
tunnel([5784, up(5784, -4), 10, 5820, up(5820, -26), 12, 5860, up(5860, -22), 10]);

// ---------------------------------------------------------------------------
// THE ABYSS  (6150 → 7050)
// The rift, then nothing at all for a long time. The scale is the content.
// ---------------------------------------------------------------------------

tunnel([
  6210, -1500, 34,
  6252, -1620, 38,
  6318, -1740, 40,
  6410, -1836, 38,
  6520, -1904, 36,
]);
blob(6660, up(6660, 26), 34, 46);
blob(6860, up(6860, 22), 28, 40);
den(6742, up(6742, -6), 22);
// A hollow spur of rock with something worth the detour inside.
vault(6950, -34, 40, 24, 11);
tunnel([6902, up(6902, -4), 11, 6944, up(6944, -26), 13]);

// ---------------------------------------------------------------------------
// ANCIENT STRUCTURE  (7050 → 7900)
// Geometry that is obviously not geological: straight corridors, square rooms,
// and a sealed heart at the end.
// ---------------------------------------------------------------------------

export const PILLAR_X: number[] = [];
for (let i = 0; i < 6; i++) {
  const x = 7160 + i * 118;
  PILLAR_X.push(x, x + 59);
  blob(x, up(x, 38), 13, 56);
  blob(x + 59, up(x + 59, 24), 10, 38);
}
// The processional: one long straight corridor through the whole complex.
tunnel([
  7120, up(7120, 8), 15,
  7320, up(7320, 6), 16,
  7520, up(7520, 6), 16,
  7700, up(7700, 6), 17,
]);
// Side vaults off it.
vault(7300, -34, 30, 20, 9);
vault(7560, -34, 28, 18, 9);
// The heart: the last room in the game.
chamber(7790, up(7790, 12), 62, 40);
tunnel([7716, up(7716, 8), 14, 7760, up(7760, 10), 16]);

// The far wall (x ~ 7960): the eastern edge of the world.
//
// There was not one. Past the heart the seabed simply flattens to about -2560
// and runs on -- measured out to 9200 with 2.5 km of open water in every column
// and nothing in it. The chart clamps a player out there onto its right-hand
// edge, so the map quietly lies about where they are, and there is no content,
// no boundary and no reason to be there.
//
// The west ends at a headland that breaches the surface. This is the same idea
// at the other end and at the other depth: the structure's outer wall rising
// off the plain past the last room, so `WORLD.endX` is a real edge rather than
// the point where the level data happens to stop.
// Sized to the COLUMN, not to a boulder. The plain out here sits at about
// -2560 under 2.5 km of water, so a 260 m blob narrows the gap and seals
// nothing — measured, it left 1896 m of open water to swim through.
blob(7990, -1290, 80, 1330);
blob(8080, -1290, 90, 1330);

export const AUTHORED: AuthoredFeature[] = F;

// ---------------------------------------------------------------------------
// Points of interest
// ---------------------------------------------------------------------------

export interface Poi {
  id: string;
  x: number;
  y: number;
  label: string;
  /** Only revealed by an active sonar ping (PRD §19). */
  hidden?: boolean;
}

export const POIS: Poi[] = [
  // West. Nothing out here is on the way to anything — these exist to be found.
  { id: 'reef', x: -1384, y: -9.6, label: 'CORAL GARDEN' },
  { id: 'reef-cache', x: -1882, y: -15, label: 'SALVAGE', hidden: true },
  { id: 'meadow', x: -2584, y: -10.8, label: 'SEAGRASS MEADOW' },
  { id: 'meadow-wreck', x: -2710, y: -7, label: 'SMALL WRECK', hidden: true },
  { id: 'wreck', x: 648, y: -92, label: 'WRECK — UNIDENTIFIED' },
  { id: 'cave-mouth', x: 1306, y: -146, label: 'CAVE SYSTEM' },
  { id: 'upgrade-cave', x: 1492, y: -207, label: 'SALVAGE', hidden: true },
  { id: 'trench', x: 2168, y: -226, label: 'TRENCH — DESCENT' },
  { id: 'arena', x: 2496, y: -556, label: 'UNKNOWN CONTACT — LARGE', hidden: true },
  { id: 'kelp', x: 2900, y: up(2900, 44), label: 'KELP FOREST' },
  { id: 'grotto', x: 3186, y: up(3186, -46), label: 'GROTTO', hidden: true },
  { id: 'city', x: 3742, y: up(3742, 52), label: 'SUNKEN CITY' },
  { id: 'city-hall', x: 4300, y: up(4300, -52), label: 'GREAT HALL', hidden: true },
  { id: 'depot', x: 4612, y: up(4612, -6), label: 'MILITARY DEPOT' },
  { id: 'berth', x: 5170, y: up(5170, 34), label: 'CAPITAL BERTH', hidden: true },
  { id: 'vents', x: 5500, y: up(5500, 62), label: 'THERMAL VENTS' },
  { id: 'vent-cache', x: 5820, y: up(5820, -34), label: 'VENT CACHE', hidden: true },
  { id: 'rift', x: 6210, y: up(6210, 46), label: 'THE RIFT' },
  { id: 'abyss-spur', x: 6950, y: up(6950, -34), label: 'HOLLOW SPUR', hidden: true },
  { id: 'ancient', x: 7247, y: up(7247, 28), label: 'ANCIENT STRUCTURE' },
  { id: 'heart', x: 7790, y: up(7790, 12), label: 'THE HEART' },
];

export const SPAWN = { x: 60, y: -34 };

/** Where the run ends. Reaching this is the win condition. */
export const FINALE = { x: 7790, y: up(7790, 12), radius: 26 };

// ---------------------------------------------------------------------------
// Regions
// ---------------------------------------------------------------------------

export type Mood = 'calm' | 'kelp' | 'city' | 'military' | 'vents' | 'abyss' | 'ancient' | 'boss';

export interface Region {
  x: number;
  name: string;
  mood: Mood;
  /**
   * Multiplied into the depth band's ambient colour so each zone reads as its
   * own place rather than as one gradient with different rocks in it.
   */
  tint: [number, number, number];
}

/** Region markers used for music/ambience cues and the HUD location readout. */
export const REGIONS: Region[] = [
  // ---- west of the spawn -------------------------------------------------
  //
  // Surveyed before anything was placed, because no audit section had ever
  // looked west of 0. Over 601 columns from -2400 the floor runs -16 to -63
  // with only 15 blocked columns, and it rises to within 1.5 m of the surface
  // in places — a genuine sunlit shelf, which is exactly the ground these two
  // zones need and the reason they go here rather than being invented land.
  //
  // The descent is east and gets darker; west is the other direction in both
  // senses. Nothing out here is on the way to anything, which is the point:
  // it is the part of the map you visit because you want to look at it.
  { x: -3000, name: 'SEAGRASS MEADOW',   mood: 'calm',     tint: [1.04, 1.12, 0.92] },
  { x: -1900, name: 'CORAL GARDEN',      mood: 'kelp',     tint: [1.16, 1.00, 0.96] },
  { x: -900,  name: 'COASTAL SHELF',     mood: 'calm',     tint: [1.00, 1.00, 1.00] },
  { x: 520,   name: 'WRECK SITE',        mood: 'calm',     tint: [1.02, 0.98, 0.94] },
  { x: 880,   name: 'MINEFIELD',         mood: 'military', tint: [0.96, 0.98, 1.04] },
  { x: 1280,  name: 'CAVE SYSTEM',       mood: 'abyss',    tint: [0.92, 0.96, 1.02] },
  { x: 1720,  name: 'PATROL ZONE',       mood: 'military', tint: [0.94, 0.99, 1.06] },
  { x: 2100,  name: 'THE TRENCH',        mood: 'abyss',    tint: [0.90, 0.95, 1.08] },
  { x: 2380,  name: 'ABYSSAL CHAMBER',   mood: 'boss',     tint: [1.05, 0.88, 0.94] },
  // Green-leaning, as a kelp forest should be — but 1.10 against 0.92 was
  // enough to put green AHEAD of blue in the inscatter, which is the one thing
  // that reads as river water rather than as a sea with weed in it.
  { x: 2720,  name: 'KELP FOREST',       mood: 'kelp',     tint: [0.97, 1.05, 0.96] },
  { x: 3520,  name: 'SUNKEN CITY',       mood: 'city',     tint: [0.98, 0.99, 1.06] },
  { x: 4400,  name: 'MILITARY ZONE',     mood: 'military', tint: [0.92, 1.00, 1.05] },
  { x: 5000,  name: 'CAPITAL BERTH',     mood: 'boss',     tint: [1.06, 0.92, 0.90] },
  { x: 5380,  name: 'THERMAL VENTS',     mood: 'vents',    tint: [1.22, 0.94, 0.78] },
  { x: 6150,  name: 'THE ABYSS',         mood: 'abyss',    tint: [0.86, 0.92, 1.12] },
  { x: 7050,  name: 'ANCIENT STRUCTURE', mood: 'ancient',  tint: [0.95, 1.06, 1.10] },
];

/**
 * SECRET ROOMS, reached through a portal on the main lane.
 *
 * The parallax lanes were briefly populated end to end, which tripled the
 * world and with it the economy — a full clear bought the upgrade tree three
 * times over. That is the wrong shape for them. The main lane carries the
 * balance; a lane behind it is worth visiting because it is *small, hidden and
 * generous*, not because there is more of it.
 *
 * So each pocket is a few hundred metres of one far lane, and nothing else in
 * that lane exists at all. A gate stands on the main lane; flying into it puts
 * the boat in the pocket at the same x, and the same gate stands there to come
 * back through. The cache sits at the far end, so the room has to be crossed.
 *
 * `gateX` is deliberately not at `x0`: a portal you can see from the entrance
 * of the room it leads to is a door, and the point is that it is a secret.
 */
export interface Pocket {
  id: string;
  /** Which parallax lane this room is cut into. */
  lane: number;
  /** Drawn x span of the populated stretch. */
  x0: number;
  x1: number;
  /** Where the gate stands, on the main lane and in the room. */
  gateX: number;
  name: string;
  /** Salvage in the cache at the far end. */
  reward: number;
  /** Multiplier on what lives in here. These rooms bite. */
  tough: number;
}

export const POCKETS: Pocket[] = [
  // The shelf: the first one is findable early and deliberately gentle, so the
  // mechanic is learned somewhere it cannot end a run.
  { id: 'p-shelf',  lane: 1, x0: -520,  x1: -160,  gateX: -300, name: 'DROWNED HOLLOW',   reward: 240,  tough: 1.0 },
  { id: 'p-coral',  lane: 1, x0: -1780, x1: -1420, gateX: -1560, name: 'CORAL VAULT',     reward: 320,  tough: 1.15 },
  { id: 'p-wreck',  lane: 2, x0: 560,   x1: 900,   gateX: 700,  name: 'THE SPLIT KEEL',   reward: 420,  tough: 1.3 },
  { id: 'p-cave',   lane: 1, x0: 1320,  x1: 1660,  gateX: 1480, name: 'BLIND GALLERY',    reward: 480,  tough: 1.35 },
  { id: 'p-kelp',   lane: 2, x0: 2860,  x1: 3220,  gateX: 3020, name: 'THE DEEP GARDEN',  reward: 560,  tough: 1.4 },
  { id: 'p-city',   lane: 1, x0: 3700,  x1: 4080,  gateX: 3880, name: 'SEALED DISTRICT',  reward: 700,  tough: 1.5 },
  { id: 'p-vents',  lane: 2, x0: 5520,  x1: 5880,  gateX: 5680, name: 'THE CHIMNEYS',     reward: 860,  tough: 1.6 },
  { id: 'p-abyss',  lane: 1, x0: 6400,  x1: 6780,  gateX: 6580, name: 'THE QUIET',        reward: 1020, tough: 1.7 },
  { id: 'p-anc',    lane: 2, x0: 7200,  x1: 7560,  gateX: 7380, name: 'INNER SANCTUM',    reward: 1300, tough: 1.9 },
];

/** The pocket a point falls inside, if any. Lane 0 never has one. */
export function pocketAt(lane: number, x: number): Pocket | null {
  if (lane <= 0) return null;
  for (const q of POCKETS) {
    if (q.lane === lane && x >= q.x0 && x <= q.x1) return q;
  }
  return null;
}

/** The gate standing at `x` on the main lane, within `r` metres. */
export function gateNear(x: number, r: number): Pocket | null {
  let best: Pocket | null = null;
  let bestD = r;
  for (const q of POCKETS) {
    const d = Math.abs(q.gateX - x);
    if (d < bestD) { bestD = d; best = q; }
  }
  return best;
}

export function regionAt(x: number): Region {
  let r = REGIONS[0];
  for (const q of REGIONS) if (x >= q.x) r = q;
  return r;
}

// ---------------------------------------------------------------------------
// Enemies
// ---------------------------------------------------------------------------

export interface EnemyZone {
  type: EnemyType;
  x0: number;
  x1: number;
  count: number;
  /** Height range to hover at, measured from `from`. */
  lift?: [number, number];
  /**
   * What `lift` is measured from. `floor` is the meshed floor at x, which is
   * right in open country but wrong anywhere the zone is built on top of
   * itself — over a city tower or a vent chimney it puts the spawn on the roof
   * instead of in the street. `profile` measures from the authored seabed
   * curve, which is where the corridors actually are. `absolute` is raw world Y.
   */
  from?: 'floor' | 'profile' | 'absolute';
  /** Swarmers spawned with the same group flock together. */
  group?: number;
  /** Bolt this spawn to the nearest rock face. */
  anchor?: boolean;
  /** Health/score multiplier, for late-game reskins of an early enemy. */
  tough?: number;
  /** Stable id, so a defeated boss stays defeated across a reload. */
  id?: string;
  boss?: boolean;
}

export const ENEMY_ZONES: EnemyZone[] = [
  // ── west: coral garden and meadow ──
  //
  // Thin on purpose. The player asked for somewhere to look at, and the reef is
  // the only zone in the game whose job is to be pretty — filling it with
  // hostiles would be answering a different request. Enough that it is not a
  // museum, and no mechanicals at all in the meadow: out there the only thing
  // moving should be grass.
  // "Thin on purpose" was too thin. The reef is the zone whose job is to be
  // looked at, and it had not one jellyfish in it — every medusa in the game
  // was in the meadow next door — so the prettiest water in the world was also
  // the emptiest-looking. Hostiles are up too: pretty is not the same as inert,
  // and a garden you can cross without being challenged is scenery, not a
  // place. The meadow is still the calm one; this is not.
  { type: 'swarmer', x0: -1850, x1: -1560, count: 18, lift: [8, 28], group: 90 },
  { type: 'swarmer', x0: -1500, x1: -1240, count: 16, lift: [8, 26], group: 91 },
  { type: 'swarmer', x0: -1200, x1: -960, count: 14, lift: [8, 24], group: 93 },
  { type: 'seeker', x0: -1700, x1: -1050, count: 9, lift: [10, 26] },
  { type: 'drone', x0: -1650, x1: -1100, count: 7, lift: [12, 28] },
  { type: 'lurker', x0: -1560, x1: -1340, count: 3, lift: [3, 10] },
  { type: 'jelly', x0: -1840, x1: -1580, count: 18, lift: [6, 22] },
  { type: 'jelly', x0: -1540, x1: -1260, count: 20, lift: [6, 24] },
  { type: 'jelly', x0: -1220, x1: -970, count: 15, lift: [6, 20] },
  { type: 'bloom', x0: -1800, x1: -1000, count: 10, lift: [16, 34] },
  // The meadow: calm, and NOT empty. No mechanicals out here — nothing that
  // shoots, which is what makes it calm — but the water is full of drifting
  // life, because a zone whose whole proposition is "somewhere quiet to look
  // at" fails completely if there is nothing in it to look at.
  { type: 'swarmer', x0: -2760, x1: -2380, count: 16, lift: [8, 24], group: 92 },
  { type: 'swarmer', x0: -2300, x1: -2000, count: 13, lift: [8, 22], group: 94 },
  { type: 'jelly', x0: -2960, x1: -2620, count: 32, lift: [5, 19] },
  { type: 'jelly', x0: -2600, x1: -2260, count: 34, lift: [5, 20] },
  { type: 'jelly', x0: -2240, x1: -1940, count: 28, lift: [5, 19] },
  { type: 'bloom', x0: -2820, x1: -2000, count: 13, lift: [10, 22] },
  // Lurkers are the one hostile the meadow can hold without stopping being
  // calm: they are ambushers, not shooters, so the water is still quiet — it
  // is just no longer guaranteed safe.
  { type: 'lurker', x0: -2700, x1: -2100, count: 3, lift: [3, 9] },

  // ── shelf and wreck ──
  // The 900 m between the reef and the start line held NOTHING. Every zone
  // west of the wreck was authored and this one was skipped, so the stretch a
  // returning player crosses most often was the one stretch with nothing in it.
  // Pitched between its neighbours: busier than the garden, lighter than the
  // minefield, and the place the reef's escalation actually lands.
  { type: 'swarmer', x0: -880, x1: -620, count: 13, lift: [8, 26], group: 95 },
  { type: 'swarmer', x0: -560, x1: -300, count: 11, lift: [8, 24], group: 96 },
  { type: 'jelly', x0: -900, x1: -560, count: 17, lift: [6, 22] },
  { type: 'jelly', x0: -520, x1: -240, count: 14, lift: [6, 20] },
  { type: 'seeker', x0: -840, x1: -260, count: 7, lift: [10, 26] },
  { type: 'drone', x0: -760, x1: -300, count: 5, lift: [12, 28] },
  { type: 'lurker', x0: -700, x1: -420, count: 2, lift: [3, 10] },
  { type: 'bloom', x0: -860, x1: -300, count: 7, lift: [14, 28] },

  { type: 'drone', x0: 470, x1: 780, count: 2, lift: [10, 26] },
  { type: 'swarmer', x0: 300, x1: 400, count: 7, lift: [12, 34], group: 1 },

  // ── MEDUSAE, THE WHOLE WAY DOWN ──────────────────────────────────────────
  //
  // Every jellyfish in the game was west of the start line, which made them a
  // feature of two pretty zones rather than a feature of the ocean — and the
  // deep is where the real ones live. They are placed thinly and high off the
  // floor here, drifting through water the player crosses rather than gathered
  // into gardens: the west is where you go to look at them, the descent is
  // where you keep meeting them. They also do the job the abyss most needs
  // doing, which is putting something alive in a room that is otherwise rock,
  // darkness and things that shoot.
  { type: 'jelly', x0: 260, x1: 760, count: 12, lift: [10, 34] },
  { type: 'jelly', x0: 900, x1: 1240, count: 11, lift: [12, 40] },
  { type: 'bloom', x0: 500, x1: 1200, count: 5, lift: [18, 40] },
  { type: 'jelly', x0: 1320, x1: 1700, count: 13, lift: [10, 36] },
  { type: 'jelly', x0: 1760, x1: 2100, count: 12, lift: [12, 38] },
  { type: 'bloom', x0: 1400, x1: 2080, count: 6, lift: [16, 40] },
  // Down the trench itself, in the shaft the player descends. Absolute depths,
  // because the trench is a hole and `lift` off the seabed would put these in
  // the rock beside it.
  { type: 'jelly', x0: 2150, x1: 2260, count: 10, lift: [-480, -230], from: 'absolute' },
  { type: 'bloom', x0: 2170, x1: 2320, count: 4, lift: [-520, -300], from: 'absolute' },
  { type: 'jelly', x0: 2760, x1: 3200, count: 14, lift: [10, 40] },
  { type: 'bloom', x0: 2800, x1: 3400, count: 6, lift: [16, 44] },
  { type: 'jelly', x0: 3560, x1: 4200, count: 15, lift: [12, 44] },
  { type: 'bloom', x0: 3700, x1: 4300, count: 6, lift: [18, 48] },
  { type: 'jelly', x0: 4460, x1: 4960, count: 12, lift: [12, 40] },
  { type: 'jelly', x0: 5100, x1: 5340, count: 9, lift: [12, 36] },
  // The vents. Hot water is where the deep sea actually crowds.
  { type: 'jelly', x0: 5420, x1: 6080, count: 18, lift: [10, 48] },
  { type: 'bloom', x0: 5500, x1: 6050, count: 8, lift: [16, 50] },
  { type: 'jelly', x0: 6200, x1: 6900, count: 20, lift: [12, 52] },
  { type: 'bloom', x0: 6250, x1: 6880, count: 9, lift: [18, 56] },
  { type: 'jelly', x0: 7050, x1: 7700, count: 18, lift: [12, 48] },
  { type: 'bloom', x0: 7100, x1: 7660, count: 7, lift: [18, 52] },

  // ── minefield ──
  { type: 'mine', x0: 880, x1: 1200, count: 15, lift: [8, 44] },
  { type: 'seeker', x0: 960, x1: 1180, count: 3, lift: [14, 36] },
  { type: 'drone', x0: 1040, x1: 1240, count: 2, lift: [16, 38] },

  // ── cave and patrol ──
  { type: 'swarmer', x0: 1330, x1: 1420, count: 8, lift: [8, 26], group: 2 },
  { type: 'lurker', x0: 1470, x1: 1530, count: 1, lift: [6, 14] },
  { type: 'drone', x0: 1700, x1: 2090, count: 5, lift: [12, 34] },
  { type: 'turret', x0: 1760, x1: 2040, count: 2, lift: [4, 16], anchor: true },
  { type: 'mine', x0: 2060, x1: 2270, count: 6, lift: [10, 40] },
  { type: 'drone', x0: 2156, x1: 2196, count: 3, lift: [-420, -250], from: 'absolute' },
  { type: 'seeker', x0: 2200, x1: 2300, count: 3, lift: [-540, -430], from: 'absolute' },

  // ── kelp forest: shoals and ambushers, no guns ──
  { type: 'swarmer', x0: 2820, x1: 2930, count: 10, lift: [10, 40], group: 3 },
  { type: 'swarmer', x0: 3060, x1: 3180, count: 12, lift: [12, 46], group: 4 },
  { type: 'swarmer', x0: 3320, x1: 3440, count: 10, lift: [10, 42], group: 5 },
  { type: 'swarmer', x0: 2960, x1: 3060, count: 10, lift: [14, 48], group: 12 },
  { type: 'lurker', x0: 3000, x1: 3020, count: 1, lift: [4, 10] },
  { type: 'lurker', x0: 3258, x1: 3278, count: 1, lift: [4, 10] },
  { type: 'lurker', x0: 3426, x1: 3446, count: 1, lift: [4, 10] },
  { type: 'drone', x0: 3200, x1: 3480, count: 3, lift: [18, 44] },

  // ── sunken city: emplacements between the towers ──
  { type: 'turret', x0: 3600, x1: 4340, count: 7, lift: [16, 74], anchor: true, from: 'profile' },
  { type: 'drone', x0: 3560, x1: 4380, count: 8, lift: [22, 76], from: 'profile' },
  { type: 'seeker', x0: 3700, x1: 4300, count: 5, lift: [14, 58], from: 'profile' },
  { type: 'swarmer', x0: 3880, x1: 4010, count: 10, lift: [14, 52], group: 6 },
  { type: 'hunter', x0: 4150, x1: 4340, count: 1, lift: [26, 66], from: 'profile' },

  // ── military zone: the real war ──
  { type: 'mine', x0: 4420, x1: 4980, count: 14, lift: [10, 60] },
  { type: 'turret', x0: 4460, x1: 4960, count: 5, lift: [6, 34], anchor: true, from: 'profile' },
  { type: 'drone', x0: 4440, x1: 4980, count: 6, lift: [20, 56], tough: 1.2 },
  { type: 'hunter', x0: 4560, x1: 4940, count: 3, lift: [24, 60] },
  { type: 'sentinel', x0: 4700, x1: 4960, count: 2, lift: [18, 56], from: 'profile' },

  // ── the berth ──
  { type: 'dreadnought', x0: 5170, x1: 5170, count: 1, lift: [26, 26], id: 'dreadnought', boss: true },
  { type: 'turret', x0: 5060, x1: 5300, count: 4, lift: [6, 22], anchor: true },
  { type: 'seeker', x0: 5080, x1: 5290, count: 4, lift: [14, 44] },

  // ── thermal vents ──
  { type: 'lurker', x0: 5610, x1: 5640, count: 1, lift: [4, 12] },
  { type: 'lurker', x0: 5970, x1: 6000, count: 1, lift: [4, 12] },
  { type: 'seeker', x0: 5400, x1: 6100, count: 7, lift: [24, 86], from: 'profile' },
  { type: 'swarmer', x0: 5680, x1: 5810, count: 12, lift: [26, 88], group: 7, from: 'profile' },
  { type: 'drone', x0: 5420, x1: 6100, count: 5, lift: [30, 92], tough: 1.4, from: 'profile' },
  { type: 'hunter', x0: 5560, x1: 6100, count: 3, lift: [34, 94], from: 'profile' },
  { type: 'turret', x0: 5460, x1: 6080, count: 5, lift: [20, 70], anchor: true, from: 'profile' },
  { type: 'sentinel', x0: 5560, x1: 6060, count: 3, lift: [30, 88], from: 'profile' },

  // ── the abyss ──
  { type: 'sentinel', x0: 6300, x1: 6560, count: 3, lift: [-1800, -1560], from: 'absolute' },
  { type: 'sentinel', x0: 6600, x1: 7000, count: 3, lift: [18, 66], tough: 1.2, from: 'profile' },
  { type: 'hunter', x0: 6400, x1: 6980, count: 4, lift: [22, 78], tough: 1.4, from: 'profile' },
  { type: 'turret', x0: 6620, x1: 6980, count: 4, lift: [6, 44], anchor: true, from: 'profile' },
  { type: 'lurker', x0: 6730, x1: 6760, count: 1, lift: [4, 12], tough: 1.6 },
  { type: 'swarmer', x0: 6580, x1: 6720, count: 12, lift: [16, 58], group: 8 },
  { type: 'swarmer', x0: 6860, x1: 6980, count: 12, lift: [16, 58], group: 10 },
  { type: 'seeker', x0: 6250, x1: 7000, count: 8, lift: [18, 62] },

  // ── ancient structure: the wardens ──
  { type: 'sentinel', x0: 7150, x1: 7660, count: 6, lift: [-4, 22], tough: 1.35, from: 'profile' },
  { type: 'turret', x0: 7140, x1: 7700, count: 8, lift: [-2, 18], anchor: true, from: 'profile' },
  { type: 'hunter', x0: 7250, x1: 7660, count: 4, lift: [-2, 20], tough: 1.6, from: 'profile' },
  { type: 'swarmer', x0: 7380, x1: 7500, count: 12, lift: [-4, 24], group: 9, from: 'profile' },
  { type: 'seeker', x0: 7180, x1: 7700, count: 7, lift: [-4, 20], from: 'profile' },
  // The last stand, right outside the heart. Everything the game has taught
  // you, in forty metres of corridor.
  { type: 'sentinel', x0: 7700, x1: 7770, count: 2, lift: [2, 24], tough: 1.8, from: 'profile' },
  { type: 'turret', x0: 7690, x1: 7780, count: 4, lift: [0, 20], anchor: true, from: 'profile' },
  { type: 'swarmer', x0: 7720, x1: 7770, count: 10, lift: [0, 20], group: 11, from: 'profile' },
];

// ---------------------------------------------------------------------------
// Sealed vaults
// ---------------------------------------------------------------------------

export interface Secret {
  id: string;
  x: number;
  /** Metres below the authored seabed the vault's centre sits. */
  down: number;
  rx: number;
  ry: number;
  /**
   * Rock left between the end of the approach shaft and the roof of the vault.
   * A torpedo bites about 7 m out of a face and a depth charge about 10, and
   * craters chain, so this is very close to "how many shots". 8 is one
   * torpedo; 20 is three; 34 wants depth charges or real patience.
   */
  seal: number;
  label: string;
  reward: PickupType;
  amount?: number;
  /**
   * Tonnes. A vault with a mass does not hand you its reward for flying into
   * it — you have to get the thing up the shaft you blew open, which is the
   * first time the seal's *size* and the load's *weight* argue with each other.
   */
  mass?: number;
}

/**
 * Rooms with no way in. Each has a shaft that runs down from open water and
 * stops dead in the rock — you can fly to the end of it and go no further. A
 * sonar ping shows what is on the other side, and the only way through is to
 * spend ordnance on it. The shaft is the whole invitation: a dead end that
 * obviously used to go somewhere is a question the player asks themselves.
 *
 * `down` is set so the vault stays buried even where the procedural relief digs
 * the seabed a full 33 m below the authored curve — otherwise a vault meant to
 * be sealed can surface in the open and give itself away.
 */
export const SECRETS: Secret[] = [
  { id: 'v-shelf',  x: 424,  down: 60, rx: 15, ry: 10, seal: 12, label: 'SEALED CACHE',   reward: 'salvage', amount: 140 },
  { id: 'v-wreck',  x: 726,  down: 64, rx: 15, ry: 10, seal: 18, label: 'BURIED LOCKER',  reward: 'torpedo', amount: 6 },
  { id: 'v-mine',   x: 1128, down: 68, rx: 16, ry: 11, seal: 20, label: 'ORDNANCE VAULT', reward: 'nitro' },
  { id: 'v-kelp',   x: 3226, down: 62, rx: 16, ry: 11, seal: 13, label: 'GROTTO VAULT',   reward: 'salvage', amount: 195, mass: 0.9 },
  { id: 'v-city',   x: 4188, down: 76, rx: 18, ry: 12, seal: 26, label: 'CITY RELIQUARY', reward: 'life' },
  { id: 'v-mil',    x: 4866, down: 80, rx: 17, ry: 11, seal: 30, label: 'HARDENED STORE', reward: 'overdrive' },
  { id: 'v-vent',   x: 5904, down: 86, rx: 17, ry: 12, seal: 34, label: 'VENT REPOSITORY', reward: 'salvage', amount: 295, mass: 1.6 },
  { id: 'v-abyss',  x: 6906, down: 94, rx: 18, ry: 12, seal: 40, label: 'THE DEEP VAULT', reward: 'aegis' },
  { id: 'v-anc',    x: 7624, down: 88, rx: 24, ry: 13, seal: 36, label: 'SEALED RELIQUARY', reward: 'salvage', amount: 375, mass: 2.4 },
];

for (const v of SECRETS) {
  const cy = up(v.x, -v.down);
  chamber(v.x, cy, v.rx, v.ry);
  // The approach: open water down to exactly `seal` metres above the roof.
  tunnel([v.x, up(v.x, DAYLIGHT), 7.5, v.x, cy + v.ry + v.seal, 8]);
}

// ---------------------------------------------------------------------------
// Rewards
// ---------------------------------------------------------------------------

export interface PickupSpec {
  /** Stable id, so a save knows this one is already collected. */
  id: string;
  type: PickupType;
  x: number;
  y: number;
  label?: string;
  amount?: number;
  /** Only exists once an active sonar ping has swept over it (PRD §19). */
  hidden?: boolean;
  /**
   * Tonnes. Present turns this from something you fly through into something
   * you have to physically drag home on the grapple. Absent is the old
   * behaviour, which is what every existing entry wants.
   */
  mass?: number;
}

export const PICKUPS: PickupSpec[] = [
  // ── west: the coral garden and the meadow ────────────────────────────────
  //
  // Deliberately weighted toward CARGO rather than pocket loot. The west has no
  // descent to pull you along and nothing gates it, so what has to make it
  // worth the trip is the haul — and with the reef outpost at -1010 both crates
  // are a short tow from a mooring rather than a 2 km slog. The hidden two are
  // the reason to bring sonar out here at all.
  { id: 'w01', type: 'salvage', x: -1108, y: -13.4, amount: 40 },
  { id: 'w02', type: 'salvage', x: -1384, y: -9.6, label: 'CORAL BLOOM', amount: 130, mass: 0.7 },
  { id: 'w03', type: 'nitro', x: -1520, y: -5 },
  { id: 'w04', type: 'salvage', x: -1882, y: -15, label: 'REEF CACHE', amount: 110, hidden: true },
  { id: 'w05', type: 'repair', x: -1774, y: -12.7 },
  { id: 'w06', type: 'salvage', x: -2008, y: -11.1, amount: 45 },
  { id: 'w07', type: 'salvage', x: -2584, y: -10.8, label: 'ANCHOR STONE', amount: 150, mass: 1.05 },
  { id: 'w08', type: 'torpedo', x: -2710, y: -7 },
  { id: 'w09', type: 'salvage', x: -2860, y: -7.2, label: 'WRECK SPOIL', amount: 120, hidden: true },

  // The first thing too heavy to pocket. Deliberately light (0.35 t, under the
  // ~0.6 t neutral-trim point) and deliberately in open water 150 m from the
  // wreck anchorage: the lesson here is only "the line exists and it pulls".
  // Trim, snagging and hauling something that actually fights you all come
  // later, once the verb is understood.
  { id: 'p00', type: 'salvage', x: 470, y: -62, label: 'CARGO POD', amount: 90, mass: 0.35 },
  { id: 'p01', type: 'torpedo', x: 646, y: -86, label: 'TORPEDOES +3' },
  { id: 'p02', type: 'salvage', x: 668, y: -92, amount: 40 },
  { id: 'p03', type: 'salvage', x: 1492, y: -206, label: 'DEEP SALVAGE', amount: 120, hidden: true },
  { id: 'p04', type: 'torpedo', x: 1560, y: -190, label: 'TORPEDOES +3' },
  { id: 'p05', type: 'salvage', x: 1310, y: -152, amount: 35 },
  { id: 'p06', type: 'nitro', x: 1700, y: -196, label: 'NITRO CANISTER' },
  { id: 'p07', type: 'torpedo', x: 2176, y: -404, label: 'TORPEDOES +3' },
  { id: 'p08', type: 'salvage', x: 2402, y: -572, amount: 60 },
  { id: 'p09', type: 'life', x: 2384, y: -598, label: 'SPARE HULL', hidden: true },

  // kelp forest
  { id: 'k01', type: 'salvage', x: 2900, y: up(2900, 46), amount: 45 },
  { id: 'k02', type: 'nitro', x: 3120, y: up(3120, 48) },
  { id: 'k03', type: 'salvage', x: 3186, y: up(3186, -44), label: 'GROTTO CACHE', amount: 150, hidden: true },
  { id: 'k04', type: 'torpedo', x: 3350, y: up(3350, 46), label: 'TORPEDOES +3' },
  { id: 'k05', type: 'repair', x: 3268, y: up(3268, -4) },

  // sunken city
  { id: 'c01', type: 'salvage', x: 3700, y: up(3700, 50), amount: 70 },
  { id: 'c02', type: 'shield', x: 3900, y: up(3900, 52) },
  { id: 'c03', type: 'salvage', x: 4082, y: up(4082, 54), amount: 70 },
  { id: 'c04', type: 'salvage', x: 4300, y: up(4300, -52), label: 'CITY TREASURY', amount: 260, hidden: true },
  { id: 'c05', type: 'nitro', x: 4000, y: up(4000, 50) },
  { id: 'c06', type: 'torpedo', x: 3812, y: up(3812, 48), amount: 4 },

  // military zone
  { id: 'm01', type: 'torpedo', x: 4612, y: up(4612, -6), label: 'AMMUNITION', amount: 6, hidden: true },
  { id: 'm02', type: 'salvage', x: 4740, y: up(4740, -30), amount: 110, hidden: true },
  { id: 'm03', type: 'repair', x: 4884, y: up(4884, -6), hidden: true },
  { id: 'm04', type: 'overdrive', x: 5060, y: up(5060, 30) },
  { id: 'm05', type: 'life', x: 5372, y: up(5372, 26), label: 'SPARE HULL' },
  { id: 'm06', type: 'salvage', x: 4966, y: up(4966, 48), amount: 80 },

  // thermal vents
  { id: 'v01', type: 'salvage', x: 5620, y: up(5620, -6), amount: 90, hidden: true },
  { id: 'v02', type: 'salvage', x: 5820, y: up(5820, -34), label: 'VENT CACHE', amount: 220, hidden: true },
  { id: 'v03', type: 'nitro', x: 5900, y: up(5900, 96) },
  { id: 'v04', type: 'torpedo', x: 5980, y: up(5980, -6), amount: 5, hidden: true },
  { id: 'v05', type: 'shield', x: 5400, y: up(5400, 54) },

  // the abyss
  { id: 'a01', type: 'salvage', x: 6520, y: up(6520, 46), amount: 120 },
  { id: 'a02', type: 'repair', x: 6742, y: up(6742, -6), hidden: true },
  { id: 'a03', type: 'salvage', x: 6950, y: up(6950, -32), label: 'SPUR CACHE', amount: 280, hidden: true },
  { id: 'a04', type: 'overdrive', x: 6600, y: up(6600, 52) },
  { id: 'a05', type: 'life', x: 6790, y: up(6790, 52) },

  // ancient structure
  { id: 'n01', type: 'salvage', x: 7300, y: up(7300, -32), label: 'VAULT', amount: 300, hidden: true },
  { id: 'n02', type: 'salvage', x: 7560, y: up(7560, -32), label: 'VAULT', amount: 300, hidden: true },
  { id: 'n03', type: 'aegis', x: 7440, y: up(7440, 10) },
  { id: 'n04', type: 'torpedo', x: 7640, y: up(7640, 6), amount: 6 },
  { id: 'n05', type: 'overdrive', x: 7716, y: up(7716, 8) },

  // ── Deep salvage ──────────────────────────────────────────────────────
  //
  // Weight at the bottom, so that the climb out — which is the best thing in
  // the game and was entirely accidental — has a reason and a load.
  //
  // Tuned against the real load-factor maths, not by feel. A Pathfinder at 3 t
  // is on quarter thrust and sinking at 3.1 m/s²: it can move these, barely,
  // badly, and the trip becomes the whole challenge. A Dray sits neutral to
  // 2 t and takes 4.6 t at a third of its acceleration. The heaviest piece is
  // deliberately past a Pathfinder's 1.2 t rating entirely.
  //
  // Values are modest on purpose. By the time anybody is down here the upgrade
  // tree is bought; what this money is FOR is the next hull, which is the only
  // sink left, so it does not need to be much.
  // Heights measured against the real floor rather than the authored curve —
  // the procedural relief runs well above `profileY` down here, and an eyeballed
  // `up(x, 34)` buried the first one 42 m inside rock.
  // Sites chosen by probe, not by eye: each is open water at the authored y,
  // reachable by the same flood fill the audit runs, and wide enough at the
  // depth the crate actually settles to for a sub and a crate to share.
  { id: 'd-vent', type: 'salvage', x: 5520, y: up(5520, 64), label: 'VENT TURBINE', amount: 140, mass: 2.6 },
  { id: 'd-rift', type: 'salvage', x: 6310, y: up(6310, 30), label: 'REACTOR CORE', amount: 190, mass: 3.4 },
  { id: 'd-abyss', type: 'salvage', x: 6960, y: up(6960, 30), label: 'SENSOR CELL', amount: 120, mass: 2.0 },
  { id: 'd-anc', type: 'salvage', x: 7210, y: up(7210, 64), label: 'ANCIENT MASS', amount: 230, mass: 4.6 },

  // One per sealed vault, placed at its centre. Hidden, so the ping that finds
  // them is also the thing that tells you the room is worth opening.
  ...SECRETS.map((v): PickupSpec => ({
    id: v.id,
    type: v.reward,
    x: v.x,
    y: up(v.x, -v.down),
    label: v.label,
    amount: v.amount,
    mass: v.mass,
    hidden: true,
  })),
];

// ---------------------------------------------------------------------------
// Bait balls
// ---------------------------------------------------------------------------

export interface BaitSpecData {
  id: string;
  x: number;
  /** Metres above the authored seabed. */
  lift: number;
  radius: number;
  height: number;
  count: number;
  spin: number;
  /** Brightness of the silver, 0..1. Bait balls are never tinted. */
  sheen: number;
  /** 'vortex' turns on the spot; 'cruise' runs a line and turns around. */
  mode: 'vortex' | 'cruise';
  /** Bioluminescence, 0..1. Raised where the light stops reaching. */
  glow: number;
  /** Cruise only: how far it runs before coming back. */
  range: number;
}

/**
 * Placed where there is room to see them and a reason to look up: the sunlit
 * kelp shelf, the streets of the city, the emptiness of the abyss. Counts are
 * per ball and they are only simulated within a couple of hundred metres.
 */
export const BAIT_BALLS: BaitSpecData[] = [
  // Coastal shelf — sunlit, the first shoals anyone meets.
  { id: 'b-shelf1', x: 300,  lift: 30, radius: 18, height: 24, count: 320, spin: 0.14, sheen: 1.00, mode: 'cruise', glow: 0,   range: 55 },
  { id: 'b-shelf2', x: 620,  lift: 42, radius: 24, height: 30, count: 460, spin: 0.12, sheen: 1.04, mode: 'vortex', glow: 0,   range: 0 },
  // Wreck and minefield.
  { id: 'b-wreck',  x: 830,  lift: 36, radius: 18, height: 24, count: 300, spin: 0.15, sheen: 0.96, mode: 'cruise', glow: 0,   range: 48 },
  { id: 'b-mine',   x: 1180, lift: 48, radius: 20, height: 26, count: 340, spin: 0.13, sheen: 0.98, mode: 'vortex', glow: 0,   range: 0 },
  // Cave mouth and patrol.
  { id: 'b-cave',   x: 1690, lift: 40, radius: 17, height: 22, count: 260, spin: 0.16, sheen: 0.92, mode: 'cruise', glow: 0.2, range: 42 },
  { id: 'b-patrol', x: 1960, lift: 46, radius: 22, height: 30, count: 380, spin: 0.12, sheen: 0.95, mode: 'cruise', glow: 0.2, range: 60 },
  // A column of fish going down the trench with you. Dark enough to glow.
  { id: 'b-trench', x: 2200, lift: 34, radius: 15, height: 44, count: 320, spin: 0.18, sheen: 0.90, mode: 'vortex', glow: 0.7, range: 0 },
  // Kelp forest — the showpiece. Big, bright, and worth stopping for.
  { id: 'b-kelp1',  x: 2860, lift: 44, radius: 28, height: 36, count: 620, spin: 0.11, sheen: 1.00, mode: 'cruise', glow: 0,   range: 70 },
  { id: 'b-kelp2',  x: 3120, lift: 52, radius: 34, height: 44, count: 820, spin: 0.09, sheen: 1.08, mode: 'vortex', glow: 0,   range: 0 },
  { id: 'b-kelp3',  x: 3340, lift: 40, radius: 22, height: 30, count: 420, spin: 0.15, sheen: 0.96, mode: 'cruise', glow: 0,   range: 58 },
  { id: 'b-kelp4',  x: 3450, lift: 50, radius: 26, height: 34, count: 540, spin: 0.12, sheen: 1.02, mode: 'vortex', glow: 0,   range: 0 },
  // Sunken city — shoals in the streets between the towers.
  { id: 'b-city1',  x: 3700, lift: 58, radius: 24, height: 34, count: 460, spin: 0.13, sheen: 0.94, mode: 'cruise', glow: 0.25, range: 64 },
  { id: 'b-city2',  x: 3980, lift: 64, radius: 26, height: 38, count: 520, spin: 0.11, sheen: 0.98, mode: 'vortex', glow: 0.25, range: 0 },
  { id: 'b-city3',  x: 4280, lift: 56, radius: 22, height: 30, count: 400, spin: 0.14, sheen: 0.92, mode: 'cruise', glow: 0.3,  range: 52 },
  // Military zone — thinner, they know better than to linger.
  { id: 'b-mil',    x: 4720, lift: 50, radius: 18, height: 26, count: 280, spin: 0.17, sheen: 0.88, mode: 'cruise', glow: 0.45, range: 46 },
  // Vents and the abyss: past here nothing is lit from above, and the shoals
  // are mostly lantern fish carrying their own light.
  { id: 'b-vent',   x: 5760, lift: 74, radius: 22, height: 30, count: 380, spin: 0.16, sheen: 1.05, mode: 'vortex', glow: 0.9, range: 0 },
  { id: 'b-abyss',  x: 6540, lift: 66, radius: 32, height: 44, count: 620, spin: 0.07, sheen: 1.12, mode: 'cruise', glow: 1.0, range: 85 },
  // No shoal in the ancient structure. Nothing this size fits down its
  // corridors, and after eight kilometres of life the last zone being empty is
  // worth more than one more shoal.
];

// ---------------------------------------------------------------------------
// Stations
// ---------------------------------------------------------------------------

export interface StationSpec {
  id: string;
  x: number;
  y: number;
  name: string;
}

/**
 * Repair, refit and save (PRD §20). One at the head of every zone and one
 * immediately before each boss, so no stretch between checkpoints is longer
 * than a couple of minutes of hard play.
 */
export const STATIONS: StationSpec[] = [
  // The west's only mooring, on the reef's eastern edge. Without it the two
  // western zones are a 2 km round trip with nowhere to sell what you find,
  // which makes hauling anything out of them a chore rather than a run.
  // Half way out. Without it the run west is 1258 m with nowhere to respawn,
  // which the audit flags and which turns one bad fight at the reef into losing
  // the whole trip. The west is optional, so it must not be punishing to try.
  { id: 's-west', x: -540, y: -13.6, name: 'WEST MOORING' },
  { id: 's-reef', x: -1108, y: -13.4, name: 'REEF OUTPOST' },
  { id: 's-shelf', x: 150, y: -46, name: 'SHELF OUTPOST' },
  { id: 's-wreck', x: 620, y: -76, name: 'WRECK ANCHORAGE' },
  { id: 's-cave', x: 1290, y: -120, name: 'CAVE MOUTH' },
  { id: 's-patrol', x: 1730, y: -170, name: 'PATROL MARKER' },
  { id: 's-trench', x: 2150, y: -204, name: 'TRENCH HEAD' },
  { id: 's-kelp', x: 2790, y: up(2790, 10), name: 'KELP STATION' },
  { id: 's-city', x: 3550, y: up(3550, 30), name: 'CITY GATE' },
  { id: 's-military', x: 4430, y: up(4430, 28), name: 'FORWARD DEPOT' },
  { id: 's-berth', x: 4980, y: up(4980, 42), name: 'BERTH APPROACH' },
  { id: 's-vents', x: 5400, y: up(5400, 16), name: 'VENT STATION' },
  { id: 's-rift', x: 6160, y: up(6160, 44), name: 'RIFT HEAD' },
  { id: 's-abyss', x: 6820, y: up(6820, 24), name: 'ABYSSAL MARKER' },
  { id: 's-ancient', x: 7120, y: up(7120, 12), name: 'THRESHOLD' },
];

// ---------------------------------------------------------------------------
// Hazards
// ---------------------------------------------------------------------------

export interface Hazard {
  kind: 'vent' | 'current';
  x: number;
  y: number;
  /** Radius for a vent; half-width for a current. */
  r: number;
  /** Vent: damage per second at the mouth. Current: metres per second of push. */
  power: number;
  /** Current only: direction of the flow. */
  dx?: number;
  dy?: number;
}

/**
 * Thermal plumes cook anything sitting in them, and they punch upward hard
 * enough to be a lift as well as a threat — the zone's whole texture is
 * deciding which plumes to ride and which to go around.
 */
export const HAZARDS: Hazard[] = [];
for (let i = 0; i < VENT_X.length; i++) {
  const x = VENT_X[i];
  HAZARDS.push({ kind: 'vent', x, y: up(x, 58), r: 22 + (i % 3) * 6, power: 15 + (i % 4) * 4 });
}
// The rift breathes: a strong downdraught on the way in, an updraught on the
// far wall, so the descent is something to fly rather than something to fall.
HAZARDS.push({ kind: 'current', x: 6260, y: up(6260, 20), r: 90, power: 16, dx: 0.15, dy: -1 });
HAZARDS.push({ kind: 'current', x: 6520, y: up(6520, 24), r: 80, power: 12, dx: 0.9, dy: 0.4 });

// ---------------------------------------------------------------------------
// Onboarding
// ---------------------------------------------------------------------------

export interface Hint {
  id: string;
  /** Shown once, the first time the sub gets within `r` of this x. */
  x: number;
  r: number;
  text: string;
  /** Desktop wording. */
  key: string;
  /** Touch wording, when the control has a different name under a thumb. */
  touch?: string;
}

/**
 * A child should never have to read a manual. Each of these fires once, in the
 * place where the thing it teaches is about to matter, and never again.
 */
export const HINTS: Hint[] = [
  { id: 'h-move', x: 90, r: 70, text: 'MOVE', key: 'WASD OR THE ARROW KEYS', touch: 'DRAG ANYWHERE ON THE LEFT' },
  { id: 'h-fire', x: 260, r: 70, text: 'CANNON', key: 'HOLD SPACE TO FIRE', touch: 'HOLD FIRE' },
  { id: 'h-nitro', x: 400, r: 80, text: 'NITRO', key: 'HOLD SHIFT TO BURN — IT REFILLS SLOWLY', touch: 'HOLD NITRO — IT REFILLS SLOWLY' },
  { id: 'h-cargo', x: 470, r: 60, text: 'CARGO', key: 'PRESS G TO GRAPPLE IT — HOLD G TO WINCH IT IN', touch: 'TAP GRAB — HOLD GRAB TO WINCH IT IN' },
  { id: 'h-sonar', x: 620, r: 90, text: 'ACTIVE SONAR', key: 'PRESS E — IT FINDS WHAT THE LAMP CANNOT', touch: 'TAP SONAR — IT FINDS HIDDEN THINGS' },
  { id: 'h-mines', x: 870, r: 90, text: 'MINEFIELD', key: 'MINES GO OFF ON CONTACT — SHOOT THEM OR GO AROUND', touch: 'MINES GO OFF ON CONTACT' },
  { id: 'h-lamp', x: 1300, r: 90, text: 'CAVE SYSTEM', key: 'L TOGGLES THE LAMP — RUNNING DARK HIDES YOU', touch: 'LAMP OFF HIDES YOU FROM MACHINES' },
  { id: 'h-trench', x: 2150, r: 110, text: 'THE TRENCH', key: 'DEEPER IS DARKER. TAKE IT SLOWLY', touch: 'DEEPER IS DARKER. TAKE IT SLOWLY' },
  { id: 'h-kelp', x: 2880, r: 120, text: 'KELP FOREST', key: 'NOTHING HERE WANTS TO KILL YOU. MOSTLY', touch: 'NOTHING HERE WANTS TO KILL YOU. MOSTLY' },
  { id: 'h-city', x: 3560, r: 110, text: 'SUNKEN CITY', key: 'GUNS ARE BOLTED TO THE TOWERS — USE THE STREETS', touch: 'GUNS ARE BOLTED TO THE TOWERS' },
  { id: 'h-berth', x: 5000, r: 130, text: 'CAPITAL CONTACT AHEAD', key: 'KILL ITS TWO WEAPON PODS FIRST', touch: 'KILL ITS TWO WEAPON PODS FIRST' },
  { id: 'h-vents', x: 5380, r: 120, text: 'THERMAL VENTS', key: 'THE PLUMES BURN — BUT THEY ALSO LIFT', touch: 'THE PLUMES BURN — BUT THEY ALSO LIFT' },
];

/** Hints that fire on an event rather than a place. */
export const EVENT_HINTS: Record<string, { text: string; key: string; touch?: string }> = {
  lock: { text: 'TARGET LOCKED', key: 'Q FIRES A TORPEDO THAT FOLLOWS IT', touch: 'TAP TORP — IT WILL FOLLOW' },
  shield: { text: 'SHIELD DOWN', key: 'BREAK OFF AND IT COMES BACK ON ITS OWN', touch: 'BREAK OFF AND IT COMES BACK' },
  station: { text: 'STATION', key: 'PRESS U HERE TO SPEND SALVAGE', touch: 'TAP DOCK HERE TO SPEND SALVAGE' },
  hull: { text: 'HULL CRITICAL', key: 'FIND A STATION — THEY REPAIR YOU FOR FREE', touch: 'FIND A STATION — REPAIRS ARE FREE' },
  cargo: { text: 'ON THE LINE', key: 'IT IS HEAVY. TAP G TO CUT IT LOOSE', touch: 'IT IS HEAVY. TAP GRAB TO CUT IT LOOSE' },
  ram: { text: 'RAMMING SPEED', key: 'AT FULL NITRO THE HULL IS A WEAPON', touch: 'AT FULL NITRO THE HULL IS A WEAPON' },
  // Taught where it happens, which for mining is the first time the boat is
  // slow enough and close enough for the beam to start on its own. The line
  // says the two conditions rather than a key, because there is no key.
  mine: { text: 'WORKING THE SEAM', key: 'HOLD STATION — DRIFT OFF AND IT STOPS', touch: 'HOLD STATION — DRIFT OFF AND IT STOPS' },
  holdfull: { text: 'HOLD FULL', key: 'LAND IT AT A STATION — THE SEAM KEEPS WHAT IS LEFT', touch: 'LAND IT AT A STATION — THE SEAM KEEPS' },
};

/** Where the octopus lairs (PRD §18). */
export const OCTOPUS = { x: 2470, y: -556, lairX: 2612, lairY: -590 };
